import { DatePipe } from '@angular/common';
import {
	Component,
	computed,
	effect,
	inject,
	input,
	signal,
} from '@angular/core';
import { DialogService } from '@context/frontend/common';
import { getUaDevice } from '@context/frontend/native/platform';
import {
	Chemical,
	ChemicalRevision,
	ChemicalUsageLog,
} from '@context/shared/types/chemical';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiButton, TuiDropdown, TuiHint, TuiIcon } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { DocumentReference, getDoc } from 'firebase/firestore';
import { ChemicalUsageLogDetailsDialogComponent } from '../../dialogs';
import { ChemicalReiBadgeComponent } from '../chemical-rei-badge/chemical-rei-badge.component';

@Component({
	imports: [
		TranslocoModule,
		TuiIcon,
		TuiButton,
		TuiHint,
		TuiDropdown,
		DatePipe,
		ChemicalReiBadgeComponent,
	],
	selector: 'ctx-guest-chemical-usage-log-row',
	templateUrl: 'guest.chemical-usage-log-row.component.html',
	styleUrl: 'guest.chemical-usage-log-row.component.scss',
	host: {
		'[attr.device]': 'device',
	},
})
export class GuestChemicalUsageLogRowComponent {
	readonly device = getUaDevice();

	private readonly dialog = inject(DialogService);

	readonly usageLog = input.required<ChemicalUsageLog>();
	readonly usageLogRef =
		input.required<DocumentReference<ChemicalUsageLog>>();

	readonly chemical = signal<Chemical | null>(null);
	readonly chemicalRevision = signal<ChemicalRevision | null>(null);
	readonly loading = signal(true);

	readonly appliedAtDate = computed(() => {
		const log = this.usageLog();
		return log.appliedAt?.toDate() ?? null;
	});

	readonly reiDate = computed(() => {
		const log = this.usageLog();

		const appliedAt = this.appliedAtDate();
		if (!appliedAt) return null;

		const reiHours = log.reiHours;
		appliedAt.setHours(appliedAt.getHours() + reiHours);
		return appliedAt;
	});

	readonly chemicalTooltip = computed(() => {
		const chemical = this.chemical();
		const revision = this.chemicalRevision();

		if (!chemical) return null;

		const parts: string[] = [`<b>${chemical.name}</b>`];

		if (revision?.regulationNumber) {
			parts.push(
				`<b>Regulation Number:</b> ${revision.regulationNumber}`,
			);
		}

		if (chemical.activeIngredient) {
			parts.push(
				`<b>Active Ingredient:</b> ${chemical.activeIngredient}`,
			);
		}

		return parts.join('\n');
	});

	readonly usageLogDetailDialog = () =>
		this.dialog.open(
			new PolymorpheusComponent(ChemicalUsageLogDetailsDialogComponent),
			ChemicalUsageLogDetailsDialogComponent.Options({
				data: { usageLog: this.usageLog(), showInternalNote: false },
			}),
		);

	constructor() {
		effect(() => {
			const log = this.usageLog();
			if (!log) return;
			this.loadChemical(log);
		});
	}

	private async loadChemical(log: ChemicalUsageLog) {
		const currentChemical = this.chemical();
		const currentRevision = this.chemicalRevision();
		if (
			currentChemical?.id === log.chemical.id &&
			currentRevision?.id === log.chemicalRevision.id
		) {
			return;
		}

		this.loading.set(true);

		try {
			const [chemicalDoc, revisionDoc] = await Promise.all([
				getDoc(log.chemical as DocumentReference<Chemical>),
				getDoc(
					log.chemicalRevision as DocumentReference<ChemicalRevision>,
				),
			]);

			if (chemicalDoc.exists()) {
				this.chemical.set(chemicalDoc.data() as Chemical);
			}

			if (revisionDoc.exists()) {
				this.chemicalRevision.set(
					revisionDoc.data() as ChemicalRevision,
				);
			}
		} catch (error) {
			console.error('Error loading chemical:', error);
		} finally {
			this.loading.set(false);
		}
	}

	readonly onViewDetails = () => this.usageLogDetailDialog().subscribe();
}
